<template>
  <div style="display: flex">
    <div style="font-weight: bold; font-size: 20px">
      <v-icon class="mr-3">mdi-playlist-play</v-icon>
      Queue Conditions
    </div>
    <v-spacer />
    <HelpButton mdx-name="DefaultQueueConditionsConfig" />
  </div>
  <v-divider class="mt-3" />
  <div class="config-page-body">
    <div style="margin-top: 40px; margin-bottom: 20px">
      <div></div>
      <v-card-subtitle>Standalone Queues</v-card-subtitle>
      <v-divider />
    </div>
    <ConditionPicker
      itemtype="default_standalone_queue_condition"
      :item="standalone_item" />
    <div style="margin-top: 40px; margin-bottom: 20px">
      <div></div>
      <v-card-subtitle>Nested Queues</v-card-subtitle>
      <v-divider />
    </div>
    <ConditionPicker
      itemtype="default_nested_queue_condition"
      :item="nested_item" />
    <v-divider
      class="mt-3"
      style="margin-bottom: 12px" />
    <div style="display: flex">
      <v-spacer />
      <v-btn
        :loading="saving_conditions"
        :disabled="!has_changes"
        color="primary"
        variant="flat"
        @click="saveConditions()">
        <v-icon class="ml-1 mr-2">mdi-content-save</v-icon>
        Save
      </v-btn>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, watch } from 'vue';

import HelpButton from '../Help-Hints/HelpButton.vue';
import ConditionPicker from '../Shared/ConditionPicker.vue';
import { $http } from '../../stores/http';

const saving_conditions = ref(false);
const has_changes = ref(false);

// Local state for conditions (no deluge needed)
const nested_conditions = ref({ play_conditions: [] });
const standalone_conditions = ref({ play_conditions: [] });

// Store initial state to detect changes
const initial_nested_conditions = ref([]);
const initial_standalone_conditions = ref([]);

// Fetch current conditions from backend on mount
onMounted(async () => {
  try {
    const response_raw = await $http('GET', '/get_default_queue_conditions');
    // $http doesn't parse JSON for GET requests, so we need to do it
    const response = JSON.parse(response_raw);

    // Set current state
    nested_conditions.value = response.nested || { play_conditions: [] };
    standalone_conditions.value = response.standalone || {
      play_conditions: [],
    };

    // Store initial state for change detection (deep copy)
    initial_nested_conditions.value = JSON.parse(
      JSON.stringify(nested_conditions.value.play_conditions)
    );
    initial_standalone_conditions.value = JSON.parse(
      JSON.stringify(standalone_conditions.value.play_conditions)
    );
  } catch (error) {
    console.error('Failed to fetch default queue conditions:', error);
  }
});

// Watch for changes to enable/disable save button
watch(
  () => [
    nested_conditions.value.play_conditions,
    standalone_conditions.value.play_conditions,
  ],
  () => {
    const current_nested = nested_conditions.value.play_conditions || [];
    const current_standalone =
      standalone_conditions.value.play_conditions || [];

    // Check if anything has changed
    const nested_changed =
      JSON.stringify(current_nested) !==
      JSON.stringify(initial_nested_conditions.value);
    const standalone_changed =
      JSON.stringify(current_standalone) !==
      JSON.stringify(initial_standalone_conditions.value);

    has_changes.value = nested_changed || standalone_changed;
  },
  { deep: true }
);

// Wrap the config objects to match ConditionPicker's expected format
const standalone_item = computed(() => ({
  conditions: standalone_conditions.value,
  item_type: 2, // Queue type
  is_standalone_queue: true, // Explicit flag for condition filtering
}));

const nested_item = computed(() => ({
  conditions: nested_conditions.value,
  item_type: 2, // Queue type
  is_nested_queue: true, // Explicit flag for condition filtering
}));

async function saveConditions() {
  saving_conditions.value = true;

  try {
    // Save both in a single call
    await $http(
      'PUT',
      '/save_default_queue_conditions',
      JSON.stringify({
        nested: {
          play_conditions: nested_conditions.value.play_conditions,
          timestamp: Math.floor(Date.now() / 1000),
        },
        standalone: {
          play_conditions: standalone_conditions.value.play_conditions,
          timestamp: Math.floor(Date.now() / 1000),
        },
      })
    );

    // Update initial state after successful save
    initial_nested_conditions.value = JSON.parse(
      JSON.stringify(nested_conditions.value.play_conditions)
    );
    initial_standalone_conditions.value = JSON.parse(
      JSON.stringify(standalone_conditions.value.play_conditions)
    );
    has_changes.value = false;
  } catch (error) {
    console.error('Failed to save default queue conditions:', error);
  } finally {
    saving_conditions.value = false;
  }
}
</script>

<style scoped>
@import './config.css';

.header {
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  color: white;
  border-radius: 5px;
}

.subheader {
  padding-left: 12px;
  margin-top: 6px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}
.subheader-text {
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  flex-shrink: 0;
  margin-right: 20px;
  opacity: 0.9;
}

.queue {
  background: rgba(0, 0, 0, 0.06);
  margin-top: 8px;
  padding: 12px;
  border-radius: 5px;
  max-height: 400px;
  overflow-y: auto;
}
</style>
